import { useCallback, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useEffectOnce, useWindowSize } from "react-use";
import { toast } from "sonner";

import { StatusCard } from "@/components/status-card";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useHeaderAdmin } from "@/hooks";
import { usGetAssessmentById } from "@/services/assessments/get-by-id";
import { useFormLesson } from "@/stores/form-exam";
import { ChevronLeftIcon } from "lucide-react";
import { QuestionItem } from "./components/question-item";
import { SectionSelectedQuestion } from "./section-selected-question";

// Tipo local para questões no formulário
interface LocalQuestion {
  id: string;
  description: string;
}

// Tipo para questões que podem vir do service ou ser locais
interface FormQuestion extends LocalQuestion {
  isLocal?: boolean;
}

export default function NewAssessment() {
  const store = useFormLesson();
  const { width } = useWindowSize();
  const { setDisableHeader } = useHeaderAdmin();
  const assessmentId = useParams().id;
  console.log(assessmentId);
  const { data, isLoading, error } = usGetAssessmentById(assessmentId || "");

  useEffectOnce(() => setDisableHeader(true));
  useEffect(() => {
    if (width <= 768) toast.warning("use this in desktop or higher screen");
  }, [width]);

  // Carregar questões do service quando os dados chegarem
  useEffect(() => {
    if (data?.data && !store.assessmentData) {
      const assessment = data.data; // Agora é um único assessment
      store.setAssessmentData(assessment);

      if (assessment.questions && assessment.questions.length > 0) {
        store.loadQuestionsFromService(assessment.questions);
      }
    }
  }, [data, store]); // Adicionado store de volta para garantir que as funções estejam atualizadas

  // Cleanup apenas quando o componente é desmontado
  useEffect(() => {
    return () => {
      store.reset();
    };
  }, []); // Array vazio - só executa no unmount

  // Tratar erros do service
  useEffect(() => {
    if (error) {
      toast.error("Erro ao carregar questões");
      console.error("Erro ao carregar questões:", error);
    }
  }, [error]);

  useEffect(() => {
    if (store.error) toast.error(store.error);
    if (store.loading) toast.warning("loading, please wait");
  }, [store.error, store.loading]);

  const handleQuestionUpdate = useCallback(
    (questionId: string, newValues: Partial<FormQuestion>) => {
      store.setQuestion(questionId, newValues);
    },
    [store]
  );

  const handleQuestionDelete = useCallback(
    (questionId: string) => {
      store.deleteQuestion(questionId);

      // Se a questão deletada era a selecionada, limpar a seleção
      if (
        store.selectedQuestion &&
        (store.selectedQuestion as any).id === questionId
      ) {
        store.setSelectedQuestion("");
      }
    },
    [store]
  );

  const handleQuestionSelect = useCallback(
    (questionId: string) => {
      store.setSelectedQuestion(questionId);
    },
    [store]
  );

  // Mostrar loading enquanto carrega os dados
  if (isLoading) {
    return (
      <div className="w-full min-h-[80vh] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p>Carregando questões...</p>
        </div>
      </div>
    );
  }

  // Se não houver questões, mostrar botão centralizado para adicionar a primeira
  if (!store.questions || store.questions.length === 0) {
    return (
      <div>
        <Header status={data?.data?.status || ""} />
        <div className="w-full min-h-[80vh] flex items-center justify-center">
          <Button
            type="button"
            variant="outline"
            className="border-dashed border-disabled py-5 px-8"
            onClick={store.addQuestion}
          >
            Adicionar primeira questão
          </Button>
        </div>
      </div>
    );
  }

  return (
    <>
      <Header status={data?.data?.status || ""} />
      <div className="grid grid-cols-2 gap-6 w-full min-h-[60vh]">
        {/* Coluna esquerda: Lista de questões */}
        <div>
          <h2 className="font-bold text-2xl mb-4">Questões</h2>

          <div className="min-w-[280px] w-full border-r h-[85vh] pr-6 flex flex-col">
            <ScrollArea className="flex-1">
              <ul className="flex flex-col gap-2 p-2">
                {(store.questions as FormQuestion[]).map((question, idx) => (
                  <li key={question.id}>
                    <QuestionItem
                      question={question}
                      index={idx}
                      isSelected={
                        !!(
                          store.selectedQuestion &&
                          (store.selectedQuestion as any).id === question.id
                        )
                      }
                      assessmentId={assessmentId || ""}
                      onSelect={handleQuestionSelect}
                      onUpdate={handleQuestionUpdate}
                      onDelete={handleQuestionDelete}
                      isNew={question.isLocal}
                    />
                  </li>
                ))}
                {/* Botão para adicionar nova questão */}
                <li>
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full mb-6 border-dashed border-disabled py-6 px-8 hover:bg-transparent hover:border-white hover:text-white"
                    onClick={store.addQuestion}
                  >
                    + Nova questão
                  </Button>
                </li>
              </ul>
            </ScrollArea>
          </div>
        </div>

        {/* Coluna direita: Formulário da questão selecionada */}
        <div className="flex-1 rounded-lg p-6 shadow flex flex-col">
          {/* Aqui virá o formulário da questão selecionada */}
          {/* O conteúdo do formulário será implementado na próxima etapa */}
          <SectionSelectedQuestion />
        </div>
      </div>
    </>
  );
}

interface AssessmentStateProps {
  status: string;
}

function Header({ status }: AssessmentStateProps) {
  const navigate = useNavigate();
  return (
    <div className="w-full h-12 flex items-center justify-between">
      <Button variant="outline" className="gap-2" onClick={() => navigate(-1)}>
        <ChevronLeftIcon />
        <p>Voltar</p>
      </Button>

      <AssessmentState status={status} />
    </div>
  );
}

function AssessmentState({ status }: AssessmentStateProps) {
  let customBg = "";
  let stateText = "";
  let customBorder = "";

  switch (status) {
    case "DISABLED":
      stateText = "Desativado";
      customBg = "#F95A4855";
      customBorder = "#F95A48";
      break;
    case "PENDING":
      customBg = "#FFBD2E55";
      stateText = "Pendente";
      customBorder = "#FFBD2E";
      break;
    case "ACTIVE":
      stateText = "Ativo";
      customBg = "#72B84A55";
      customBorder = "#72B84A";
      break;
  }

  return (
    <StatusCard
      className="h-9 py-2 flex-shrink-0 min-w-[100px]"
      customBg={customBg}
      customBorder={customBorder}
    >
      {stateText}
    </StatusCard>
  );
}
